/*
 * -----------------------------------------------------------------------
 *  <copyright file="panels-overview-grid/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import cn from 'clsx';
// eslint-disable-next-line max-len
import AddOverviewPanelsDialog from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/panels-overview-grid/add-overview-panels-dialog';
import {
    IPanelsOverviewGrid,
    StudyOverviewPanel,
} from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/panels-overview-grid/panels-overview-grid.interface';
import PanelService from '@components/central-monitoring/panels/add-panel/service';
import EditableGrid from '@components/core/editable-grid';
import { ExtraMenuItem, ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import { PanelType } from 'types/graphql-global-types';
import { useAuthorization, useBoolean, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { GET_PANELS } from '@queries/central-monitoring';
import { getPanels, getPanelsVariables } from '@queries/generated/central-monitoring.generated';
// eslint-disable-next-line max-len
import { StudyFields_Study_studyOverviewTableConfiguration_StudyOverviewTableConfigurationType_panelConfigurations_StudyOverviewPanelConfigurations as StudyOverviewPanelConfigurations } from '@queries/generated/fragments.generated';

const PanelsOverviewGrid = ({
    study,
    state,
    setState,
    disabledSaveUndoButtons,
    title,
    isStudy,
}: IPanelsOverviewGrid) => {
    const currentOverviewConfigurationType = isStudy
        ? 'studyOverviewTableConfigurationInputType'
        : 'studySiteOverviewTableConfigurationInputType';
    const { t } = useTranslation();
    const user = useAuthorization();

    const [panels, setPanels] = useState<StudyOverviewPanel[]>([]);
    const [allPanels, setAllPanels] = useState<StudyOverviewPanel[]>([]);
    const showAddRowsDialog = useBoolean(false);
    const { data } = useQuery<getPanels, getPanelsVariables>(GET_PANELS, {
        variables: {
            organisationId: user.organisationId,
            studyId: study.id ?? Constants.ObjectId.Empty,
        },
    });

    const assignOrders = (filteredPans: StudyOverviewPanel[]) => {
        const orderedPanels: StudyOverviewPanel[] = [];
        const items = state?.[currentOverviewConfigurationType]?.panelConfigurations;

        if (state) {
            items?.forEach((panConfig: StudyOverviewPanelConfigurations) => {
                filteredPans.forEach((filterPan: StudyOverviewPanel) => {
                    if (panConfig.panelId === filterPan.panelId) {
                        orderedPanels.push({
                            ...filterPan,
                            order: panConfig.panelOrder,
                        });
                    }
                });
            });
        }
        return orderedPanels;
    };

    useEffect(() => {
        if (data?.panels) {
            const panelItems: StudyOverviewPanel[] = data.panels.map((panel) => {
                const model = PanelService.getPanelModel(panel?.modelParams);
                return {
                    active: panel?.active,
                    panelId: panel?.id,
                    plotTitle: panel?.graph?.plotTitle,
                    model: panel?.panelType === PanelType.MODEL ? t(`cm:${model}`) : '',
                };
            });

            const overviewPanelsModels = [
                t('admin:NEGATIVE_BINOMIAL'),
                t('admin:FUNNEL'),
                t('admin:TRI_OPERATIONAL'),
                t('admin:RATE_GROUP'),
            ];

            const overviewPanels = panelItems.filter((panel) => overviewPanelsModels.includes(panel.model));

            setAllPanels(overviewPanels);

            const items = state?.[currentOverviewConfigurationType]?.panelConfigurations;
            const panelConfigurationsIds = items?.map((pan: any) => pan?.panelId);
            const filteredPanels = overviewPanels.filter((panel: StudyOverviewPanel) =>
                panelConfigurationsIds?.includes(panel.panelId),
            );

            const orderedPanels = assignOrders(filteredPanels);

            setPanels(orderedPanels);
        }
    }, [data, state]);

    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.expandMenu,
            customClassName: 'grid-header__item--always-visible',
            expandMenuItems: [
                {
                    label: isStudy ? 'admin:addRow' : 'admin:addPanel',
                    onClick: () => showAddRowsDialog.setTrue(),
                    icon: ['fal', 'add'],
                    enabled: true,
                },
            ],
        },
    ];

    const makeNewState = (panelsState: any[]) => {
        disabledSaveUndoButtons.setFalse();

        setState({
            ...state,
            [currentOverviewConfigurationType]: {
                ...state?.[currentOverviewConfigurationType],
                panelConfigurations: panelsState,
            },
        });
    };

    const addNewPanels = (closeModal: boolean, selectedItems: StudyOverviewPanel[]) => {
        const { panelConfigurations: configs } = state[currentOverviewConfigurationType];
        const newPanelsItems = selectedItems.map((item: StudyOverviewPanel, index: number) => ({
            panelId: item.panelId,
            panelOrder: configs.length + index + 1,
        }));

        const newPanelConfigs = configs.concat(newPanelsItems);

        makeNewState(newPanelConfigs);

        if (closeModal) {
            showAddRowsDialog.setFalse();
        }
    };

    return (
        <div>
            {showAddRowsDialog.value && (
                <AddOverviewPanelsDialog
                    title={isStudy ? t('admin:addRowS') : t('admin:addPanelS')}
                    onClose={() => showAddRowsDialog.setFalse()}
                    items={allPanels}
                    currentItems={panels}
                    onAssign={addNewPanels}
                />
            )}
            <EditableGrid
                customKey="panels-reorder-grid"
                className={cn('panels-grid', { study: isStudy, site: !isStudy })}
                items={panels}
                editable={false}
                extraMenuItems={extraMenuItems}
                columns={[
                    {
                        label: t('admin:plotTitle'),
                        field: 'plotTitle',
                        id: 'study-row-overview-plot-title',
                        minWidth: 300,
                        show: true,
                        showInactiveIcon: (panel: StudyOverviewPanel) => panel.active === false,
                    },
                    {
                        label: t('admin:model'),
                        field: 'model',
                        id: 'study-row-overview-model',
                        minWidth: 300,
                        show: true,
                    },
                ]}
                title={title}
                setState={(pansConfigurations) => {
                    const convertPanelState = pansConfigurations?.map((pan: StudyOverviewPanel) => ({
                        panelId: pan.panelId,
                        panelOrder: pan.order,
                    }));
                    makeNewState(convertPanelState);
                }}
                lineHeight="19px"
            />
        </div>
    );
};

export default PanelsOverviewGrid;
